import { useNotification } from 'src/providers/notification.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { FormProvider, useForm } from 'react-hook-form'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ProjectService } from 'src/services/project.service'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import {
  CurecallBanner,
  CurecallButton,
  CurecallConfirmationModal,
  ECurecallButtonAppearance,
} from '@honestica/curecall-ui'
import {
  EPermissionGrantActions,
  EPermissionGrantSubjects,
  TPermission,
  TPermissionFormValues,
} from './permissions.type'
import { shortenGrantActions } from './permissions.util'
import { PermissionsTableFields } from './permissions-table-fields.component'
import { PermissionHeadingFields } from './permissions-heading-fields.component'
import { PermissionIpsWhitelistFields } from './permissions-ips-whitelist.component'

function PermissionForm() {
  return (
    <div className="c-relative c-flex c-flex-col c-gap-8">
      <PermissionHeadingFields />
      <PermissionsTableFields />
      <PermissionIpsWhitelistFields />
    </div>
  )
}

function AddPermissionForm() {
  const { t } = useTranslation()
  const methods = useForm<TPermissionFormValues>({
    defaultValues: { specialAuthorizations: [], grants: [], name: '' },
  })
  const { handleSubmit } = methods
  const { project } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)

  const onSubmit = async (formData: TPermissionFormValues) => {
    setIsButtonLoading(true)
    try {
      const result = await ProjectService.postPermission(project.id, {
        ...formData,
        grants: shortenGrantActions(formData.grants),
      })
      if (queryClient.getQueryData(['permissions', project.id]) !== undefined) {
        queryClient.setQueryData(['permissions', project.id], (oldData: any) => {
          return [...(oldData || []), result]
        })
      }

      notify(
        {
          title: t('pages.configuration.permissions.submit-success-title', { isUpdating: false }),
          message: t('pages.configuration.permissions.submit-success-message'),
          appearance: 'SUCCESS',
        },
        15000,
      )
      navigate(`/${project.id}/configuration/permissions`)
    } catch (err: any) {
      notify(
        {
          title: t('pages.configuration.permissions.submit-error', { isUpdating: false }),
          message: err,
          appearance: 'ERROR',
        },
        15000,
      )
    } finally {
      setTimeout(() => {
        setIsButtonLoading(false)
      }, 2000)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <PermissionForm />

        <div className="c-mt-16 c-mb-6 c-flex c-justify-center c-gap-4">
          <CurecallButton
            appearance={ECurecallButtonAppearance.CANCEL}
            type="button"
            isLoading={isButtonLoading}
            onClick={() => navigate(-1)}
          >
            {t('common.actions.cancel')}
          </CurecallButton>
          <CurecallButton type="submit" isLoading={isButtonLoading}>
            {t('pages.configuration.permissions.create-permission')}
          </CurecallButton>
        </div>
      </form>
    </FormProvider>
  )
}

function UpdatePermissionForm({
  defaultValues,
  permissionId,
}: {
  defaultValues: TPermissionFormValues
  permissionId: number
}) {
  const { t } = useTranslation()
  const methods = useForm<TPermissionFormValues>({ defaultValues })
  const { handleSubmit } = methods
  const { project, currentPermission } = useCurrentProject()
  const navigate = useLocalizedNavigate()
  const { notify } = useNotification()
  const queryClient = useQueryClient()

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false)
  const [formDataToConfirm, setFormDataToConfirm] = useState<TPermissionFormValues>()

  const onSubmit =
    (forceUpdate = false) =>
    async (formData: TPermissionFormValues) => {
      const newGrants = shortenGrantActions(formData.grants)
      if (
        !forceUpdate &&
        currentPermission.id === permissionId &&
        !newGrants
          ?.find((grant) => grant.subject === EPermissionGrantSubjects.USER_MANAGEMENT)
          ?.actions?.includes(EPermissionGrantActions.ALL) &&
        !newGrants
          ?.find((grant) => grant.subject === EPermissionGrantSubjects.ALL)
          ?.actions?.includes(EPermissionGrantActions.ALL)
      ) {
        setFormDataToConfirm(formData)
        setIsOpenConfirmModal(true)
        return
      }
      try {
        const result = await ProjectService.patchPermission(project.id, permissionId, {
          ...formData,
          grants: newGrants,
        })
        if (queryClient.getQueryData(['permissions', project.id]) !== undefined) {
          queryClient.setQueryData(['permissions', project.id], (oldData: any) => {
            if (!oldData) return
            return oldData.map((existingPermission: TPermission) =>
              existingPermission.id !== result.id ? existingPermission : result,
            )
          })
        }
        queryClient.setQueryData(['permission', project.id, permissionId], (oldData: any) => {
          if (!oldData) return
          return result
        })
        notify(
          {
            title: t('pages.configuration.permissions.submit-success-title', { isUpdating: true }),
            message: t('pages.configuration.permissions.submit-success-message'),
            appearance: 'SUCCESS',
          },
          15000,
        )
        navigate(`/${project.id}/configuration/permissions`)
      } catch (err: any) {
        notify(
          {
            title: t('pages.configuration.permissions.submit-error', { isUpdating: true }),
            message: err,
            appearance: 'ERROR',
          },
          15000,
        )
      } finally {
        setTimeout(() => {
          setIsButtonLoading(false)
        }, 2000)
      }
    }

  return (
    <FormProvider {...methods}>
      {currentPermission?.id === permissionId && (
        <CurecallBanner appearance={'WARNING'}>
          {t('components.permissions.edit-currently-used-permission-warning')}
        </CurecallBanner>
      )}

      <CurecallConfirmationModal
        color="ALERT"
        title={t('components.permissions.confirmation-modal.title')}
        isOpen={isOpenConfirmModal}
        cancelButtonLabel={t('components.permissions.confirmation-modal.cancel')}
        confirmButtonLabel={t('components.permissions.confirmation-modal.confirm')}
        onCancel={() => setIsOpenConfirmModal(false)}
        onConfirm={() => {
          formDataToConfirm && onSubmit(true)(formDataToConfirm).then(() => setIsOpenConfirmModal(false))
        }}
      >
        {t('components.permissions.confirmation-modal.description')}
      </CurecallConfirmationModal>
      <form onSubmit={handleSubmit(onSubmit(false))}>
        <PermissionForm />

        <div className="c-mt-16 c-mb-6 c-flex c-justify-center c-gap-4">
          <CurecallButton
            appearance={ECurecallButtonAppearance.CANCEL}
            type="button"
            isLoading={isButtonLoading}
            onClick={() => navigate(`/${project.id}/configuration/permissions`)}
          >
            {t('common.actions.cancel')}
          </CurecallButton>
          <CurecallButton type="submit" isLoading={isButtonLoading}>
            {t('pages.configuration.permissions.validate-changes')}
          </CurecallButton>
        </div>
      </form>
    </FormProvider>
  )
}

export { AddPermissionForm, UpdatePermissionForm }
